import React, { useState, useEffect } from 'react';
import { usePlan } from '../contexts/PlanContext';
import { useToast } from '../contexts/ToastContext';
import api from '../services/axios';
import './ModelSelector.css';

const ModelSelector = () => {
  const { planType, isFeatureAvailable } = usePlan();
  const { showError, showSuccess } = useToast();
  
  // Add debugging
  console.log('ModelSelector: planType =', planType);
  console.log('ModelSelector: isFeatureAvailable("modelSelection") =', isFeatureAvailable('modelSelection'));
  
  const [availableModels, setAvailableModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedChatModel, setSelectedChatModel] = useState('');
  const [selectedDocumentModel, setSelectedDocumentModel] = useState('');

  useEffect(() => {
    console.log('ModelSelector: useEffect triggered, planType =', planType);
    console.log('ModelSelector: isFeatureAvailable("modelSelection") =', isFeatureAvailable('modelSelection'));
    if (isFeatureAvailable('modelSelection')) {
      console.log('ModelSelector: Feature available, calling loadModelPreferences');
      loadModelPreferences();
    } else {
      console.log('ModelSelector: Feature not available, skipping loadModelPreferences');
    }
  }, [planType, isFeatureAvailable]);

  const loadModelPreferences = async () => {
    console.log('ModelSelector: loadModelPreferences called');
    try {
      console.log('ModelSelector: Making API call to /users/available-models');
      const response = await api.get('/users/available-models');
      console.log('ModelSelector: API response received:', response.data);
      setAvailableModels(response.data.data.availableModels);
      setSelectedChatModel(response.data.data.currentPreferences.chatModel);
      setSelectedDocumentModel(response.data.data.currentPreferences.documentModel);
    } catch (error) {
      console.error('ModelSelector: Error loading model preferences:', error);
      console.error('ModelSelector: Error response:', error.response?.data);
      showError('Failed to load model preferences. Please try again.');
    }
  };

  const updatePreferences = async () => {
    setLoading(true);
    
    try {
      await api.put('/users/model-preferences', {
        chatModel: selectedChatModel,
        documentModel: selectedDocumentModel
      });
      
      showSuccess('Model preferences updated successfully!');
    } catch (error) {
      console.error('Error updating preferences:', error);
      showError(error.response?.data?.message || 'Failed to update preferences. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getModelDisplayName = (modelName) => {
    const displayNames = {
      'gpt-4o': 'GPT-4o',
      'gpt-4.1': 'GPT-4.1',
      'claude-sonnet-3.5': 'Claude Sonnet 3.5',
      'claude-sonnet-4': 'Claude Sonnet 4'
    };
    return displayNames[modelName] || modelName;
  };

  if (!isFeatureAvailable('modelSelection')) {
    return null;
  }

  return (
    <div className="model-selector">
      <div className="model-sections">
        <div className="model-section">
          <h4>Chat Conversations</h4>
          <p className="model-description">Used for real-time chat conversations and Q&A</p>
          <select 
            value={selectedChatModel} 
            onChange={(e) => setSelectedChatModel(e.target.value)}
            className="model-select"
          >
            {availableModels.map(model => (
              <option key={model} value={model}>
                {getModelDisplayName(model)}
              </option>
            ))}
          </select>
        </div>
        
        <div className="model-section">
          <h4>Document Generation</h4>
          <p className="model-description">Used for creating Account Briefs, Battle Cards, and Objection Handling documents</p>
          <select 
            value={selectedDocumentModel} 
            onChange={(e) => setSelectedDocumentModel(e.target.value)}
            className="model-select"
          >
            {availableModels.map(model => (
              <option key={model} value={model}>
                {getModelDisplayName(model)}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      <button 
        onClick={updatePreferences}
        disabled={loading}
        className="update-button"
      >
        {loading ? 'UPDATING...' : 'UPDATE PREFERENCES'}
      </button>
      
      <div className="model-info">
        <h5>Model Information:</h5>
        <ul>
          <li><strong>GPT-4o:</strong> Fastest and most cost-effective, great for general conversations</li>
          <li><strong>GPT-4.1:</strong> Most capable model, excellent for complex analysis and document generation</li>
          <li><strong>Claude Sonnet 3.5:</strong> Strong reasoning and analysis capabilities</li>
          <li><strong>Claude Sonnet 4:</strong> Most advanced Claude model, best for complex tasks</li>
        </ul>
      </div>
    </div>
  );
};

export default ModelSelector; 